import React, {useId} from 'react';
import {
  Dropdown as PathDropdown,
  DropdownItem as PathDropdownItem,
} from '@ellucian/react-design-system/core';
import {SelectProps} from '@mui/material';
import {useField} from 'formik';

import 'stylesheets/components/shared-dropdown.scss';

interface DropdownItem {
  label: string;
  value: string;
  LeftIconComponent?: React.ReactNode;
  RightIconComponent?: React.ReactNode;
  disabled?: boolean;
}

export type Props = Omit<SelectProps<string>, 'size' | 'error'> & {
  size?: 'small' | 'default';
  options: DropdownItem[] | string[];
  error?: boolean | string;
  cy?: string;
  testid?: string;
};

const EllucianDropdown = ({
  className,
  error,
  size = 'default',
  testid,
  cy,
  ...dropdownProps
}: Props) => {
  const errorId = useId();
  const hasError = !!error;

  return (
    <div>
      <PathDropdown
        {...dropdownProps}
        className={className}
        size={size}
        inputProps={{
          'data-testid': testid,
          'data-cy': cy,
          ...(hasError && {
            'aria-invalid': true,
            'aria-describedby': errorId,
          }),
          ...dropdownProps.inputProps,
        }}
      >
        {dropdownProps.options.map((option: DropdownItem | string) => {
          const isString = typeof option === 'string';

          return (
            <PathDropdownItem
              key={isString ? option : option.value}
              {...(isString ? {label: option, value: option} : option)}
            />
          );
        })}
      </PathDropdown>
      {hasError && (
        <div
          id={errorId}
          className="shared-error-text"
          role="alert"
          data-testid="shared-text-input-error"
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default EllucianDropdown;

export const FormikEllucianDropdown = (props: {name: string} & Props) => {
  const [field, meta] = useField(props.name);

  return (
    <EllucianDropdown
      {...props}
      {...field}
      error={meta.touched ? meta.error : ''}
      {...(props.onChange && {
        onChange: (e) => {
          field.onChange(e);
          props.onChange?.(e, null);
        },
      })}
    />
  );
};
